import { Controller } from "@hotwired/stimulus"
import debounce from "debounce"
import { closeRailsuiModal as closeModalById, escapeModalId } from "./report_dashboard_modal"

const PER_PAGE = 10
const SCROLL_THRESHOLD_PX = 50
const MAX_SCROLL_FILL_ATTEMPTS = 10
const SEARCH_DEBOUNCE_MS = 400

export default class extends Controller {
  static targets = [
    "filterForm", "assessmentList", "searchField", "tableContent",
    "selectedFiltersContainer", "filtersMeta", "clearFiltersButton", "assessmentHeader", "filterBadgeTemplate", "fetchErrorTemplate",
    "checkbox", "selectAllCheckbox", "selectedIdsField", "availableCount", "liveRegion", "viewResultsButton",
  ]

  static values = {
    sortColumn: String,
    sortDirection: String,
    modalId: { type: String, default: "selectAssessments" },
  }

  connect() {
    if (!this.hasTableContentTarget || !this.hasSearchFieldTarget || !this.hasFilterFormTarget) return

    this.page = 1
    this.loading = false
    this.endOfResults = false
    this.fetchGeneration = 0
    this.selectedIds = new Set()
    this.boundOnScroll = () => this.onScroll()
    this.boundOnSearchInput = () => {
      this.updateFiltersMetaVisibility()
      this.debouncedSearch()
    }
    this.tableContentTarget.addEventListener("scroll", this.boundOnScroll)
    this.debouncedSearch = debounce(() => this.onSearch(), SEARCH_DEBOUNCE_MS)
    this.searchFieldTarget.addEventListener("input", this.boundOnSearchInput)
    this.updateSelectedFiltersDisplay()
    this.updateSelectedIds()
    this.updateViewResultsButtonState()
    this.handleEscape = this.handleEscape.bind(this)
    document.addEventListener("keydown", this.handleEscape)
  }

  loadModalData() {
    this.resetPagination()
    this.fetchFilteredAssessments()
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleEscape)
    if (this.hasTableContentTarget && this.boundOnScroll) {
      this.tableContentTarget.removeEventListener("scroll", this.boundOnScroll)
    }
    if (this.hasSearchFieldTarget && this.boundOnSearchInput) {
      this.searchFieldTarget.removeEventListener("input", this.boundOnSearchInput)
    }
    this.debouncedSearch?.clear?.()
  }

  modalScrollContainer() {
    const modalRoot = document.getElementById(this.modalIdValue)
    return modalRoot?.querySelector(".aquifer-modal--wide")
  }

  open(event) {
    if (event) event.preventDefault()
    this.loadModalData()
  }

  close(event) {
    if (event) event.preventDefault()
    this.resetFilterState()
    this.closeRailsuiModal()
  }

  closeRailsuiModal() {
    closeModalById(this.application, this.modalIdValue)
  }

  clearFilters(event) {
    if (event) event.preventDefault()
    this.resetFilterState()
    this.fetchFilteredAssessments()
  }

  resetFilterState() {
    this.clearAssessmentDropdowns()
    this.filterFormTarget.reset()
    this.clearDatePickers()
    this.resetPagination()
    this.clearSelectedFilters()
    this.assessmentListTarget.innerHTML = ""
    this.selectedIdsFieldTarget.value = ""
    this.selectedIds = new Set()
    this.updateViewResultsButtonState()
    this.updateSelectAllCheckboxState()
  }

  applyFilter() {
    this.resetPagination()
    this.selectedIdsFieldTarget.value = ""
    this.updateSelectedFiltersDisplay()
    this.selectedIds = new Set()
    this.updateViewResultsButtonState()
    this.updateSelectAllCheckboxState()
    this.fetchFilteredAssessments()
  }

  onScroll() {
    if (this.loading || this.endOfResults) return
    const container = this.tableContentTarget
    const isScrollable = container.scrollHeight > container.clientHeight + 1
    const nearBottom = isScrollable &&
      container.scrollHeight - container.scrollTop <= container.clientHeight + SCROLL_THRESHOLD_PX
    if (nearBottom) {
      this.page++
      this.fetchFilteredAssessments()
    }
  }

  onSearch() {
    this.resetPagination()
    this.fetchFilteredAssessments()
  }

  fetchFilteredAssessments() {
    const fetchGeneration = ++this.fetchGeneration
    this.loading = true

    if (this.page === 1) {
      this.setTableLoading(true)
      this.announce("Loading assessments.")
    }

    const formData = this.buildFormData()
    const queryString = new URLSearchParams(formData).toString()

    fetch(`/report_dashboard/filter_assessments?${queryString}`, {
      headers: { "Accept": "text/html" }
    })
      .then((response) => {
        if (!response.ok) throw new Error("Failed to load assessments")
        return response.text()
      })
      .then((html) => {
        if (fetchGeneration !== this.fetchGeneration) return
        this.handleAssessmentResponse(html)
      })
      .catch(() => {
        if (fetchGeneration !== this.fetchGeneration) return
        this.showFetchError()
      })
  }

  showFetchError() {
    this.loading = false
    this.setTableLoading(false)
    this.setAssessmentHeaderVisible(false)
    if (this.hasAvailableCountTarget) {
      this.availableCountTarget.textContent = "0 Available"
    }
    if (!this.hasAssessmentListTarget) return

    this.assessmentListTarget.innerHTML = this.hasFetchErrorTemplateTarget
      ? this.fetchErrorTemplateTarget.innerHTML.trim()
      : ""
    this.announce("Unable to load assessments. Please try again.")
  }

  buildFormData() {
    const formData = new FormData(this.filterFormTarget)
    formData.append("page", this.page)

    if (this.hasSortColumnValue && this.sortColumnValue) {
      formData.append("sort_column", this.sortColumnValue)
    }

    if (this.hasSortDirectionValue && this.sortDirectionValue) {
      formData.append("sort_direction", this.sortDirectionValue)
    }

    ["saved_report_id", "group_id", "token"].forEach((param) => {
      if (!formData.has(param)) {
        const value = this.getParamFromFormOrUrl(param)
        if (value) formData.append(param, value)
      }
    })

    return formData
  }

  getParamFromFormOrUrl(param) {
    const input = this.filterFormTarget.querySelector(`[name='${param}']`)
    if (input?.value) return input.value

    return new URLSearchParams(window.location.search).get(param)
  }

  handleAssessmentResponse(html) {
    const trimmed = html.trim()
    const hasAssessments = trimmed.includes("data-assessment-id")
    let insertedRowCount = 0

    if (this.page === 1) {
      this.assessmentListTarget.innerHTML = ""
    }

    if (trimmed === "") {
      this.endOfResults = true
    } else if (hasAssessments) {
      insertedRowCount = this.insertAssessmentsFromHtml(trimmed)
      if (insertedRowCount < PER_PAGE) {
        this.endOfResults = true
      }
    } else {
      this.assessmentListTarget.insertAdjacentHTML("beforeend", html)
      this.setAssessmentHeaderVisible(false)
      this.availableCountTarget.textContent = `0 Available`
      this.announce("No assessments fit your criteria. Please adjust your filters.")
      this.endOfResults = true
    }

    this.loading = false
    this.setTableLoading(false)

    if (hasAssessments && this.page === 1 && this.hasAvailableCountTarget) {
      this.announce(this.availableCountTarget.textContent)
    }

    if (this.page === 1) {
      this.tableContentTarget.scrollTop = 0
      const modal = this.modalScrollContainer()
      if (modal) modal.scrollTop = 0
    }

    if (hasAssessments && !this.endOfResults) {
      this.ensureTableScrollable()
    }
  }

  ensureTableScrollable() {
    requestAnimationFrame(() => {
      if (this.loading || this.endOfResults) return
      if ((this.scrollFillAttempts || 0) >= MAX_SCROLL_FILL_ATTEMPTS) return

      const container = this.tableContentTarget
      if (container.scrollHeight > container.clientHeight + 1) return

      this.scrollFillAttempts = (this.scrollFillAttempts || 0) + 1
      this.page++
      this.fetchFilteredAssessments()
    })
  }

  setAssessmentHeaderVisible(visible) {
    if (!this.hasAssessmentHeaderTarget) return

    this.assessmentHeaderTarget.classList.toggle("hidden", !visible)
  }

  setTableLoading(isLoading) {
    if (!this.hasTableContentTarget) return

    this.tableContentTarget.classList.toggle("is-loading", isLoading)
    this.tableContentTarget.setAttribute("aria-busy", String(isLoading))
  }

  insertAssessmentsFromHtml(html) {
    const doc = new DOMParser().parseFromString(`<table><tbody>${html}</tbody></table>`, "text/html")
    const rows = doc.querySelectorAll("tr[data-assessment-id]")

    let insertedRowCount = 0
    rows.forEach((row) => {
      const id = row.dataset.assessmentId
      if (this.assessmentListTarget.querySelector(`tr[data-assessment-id="${escapeModalId(id)}"]`)) return

      this.assessmentListTarget.insertAdjacentHTML("beforeend", row.outerHTML)
      insertedRowCount += 1
    })

    const meta = doc.querySelector("#filtered-assessment-data")
    const assessmentIds = meta?.dataset?.assessmentIds ||
      html.match(/data-assessment-ids="([^"]*)"/)?.[1]

    if (assessmentIds) {
      this.allAssessmentIds = assessmentIds.split(",").map(Number).filter(Boolean)
      this.totalCount = this.allAssessmentIds.length

      if (this.hasAvailableCountTarget) {
        this.availableCountTarget.textContent = `${this.totalCount} Available`
      }
    }

    this.setAssessmentHeaderVisible(true)

    this.checkboxTargets.forEach((checkbox) => {
      const id = parseInt(checkbox.closest("tr").dataset.assessmentId, 10)
      checkbox.checked = this.selectedIds.has(id)
    })

    this.updateSelectAllCheckboxState()

    return insertedRowCount
  }

  clearDatePickers() {
    this.filterFormTarget.querySelectorAll("[data-controller~='railsui-date-picker']").forEach((wrapper) => {
      this.application
        .getControllerForElementAndIdentifier(wrapper, "railsui-date-picker")
        ?.picker?.clear()
    })
  }

  resetPagination() {
    this.page = 1
    this.endOfResults = false
    this.scrollFillAttempts = 0
  }

  clearSelectedFilters() {
    this.filterFormTarget.querySelector("input[name='selected_disciplines']").value = ""
    this.filterFormTarget.querySelector("input[name='selected_purposes']").value = ""
    this.updateSelectedFiltersDisplay()
  }

  clearAssessmentDropdowns() {
    this.filterFormTarget.querySelectorAll("[data-controller~='railsui-dropdown-menu']").forEach((shell) => {
      const menuController = this.application.getControllerForElementAndIdentifier(shell, "railsui-dropdown-menu")
      menuController?.clearSelections()
    })
  }

  updateSelectedFiltersDisplay() {
    const container = this.selectedFiltersContainerTarget
    container.innerHTML = ""

    const disciplines = this.filterFormTarget.querySelector("input[name='selected_disciplines']").value.split(",").filter(Boolean)
    const purposes = this.filterFormTarget.querySelector("input[name='selected_purposes']").value.split(",").filter(Boolean)
    const titleize = (str) => str.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())

    const filters = [
      ...disciplines.map((value) => ({ type: "discipline", value })),
      ...purposes.map((value) => ({ type: "purpose", value })),
    ]

    if (filters.length === 0 || !this.hasFilterBadgeTemplateTarget) {
      this.updateFiltersMetaVisibility()
      return
    }

    const list = document.createElement("ul")
    list.className = this.filterFormTarget.dataset.filterBadgesClass ||
      "m-0 flex list-none flex-wrap items-center gap-2 p-0"
    list.setAttribute("role", "list")

    filters.forEach((filter) => {
      const label = titleize(filter.value)
      const badge = this.filterBadgeTemplateTarget.content.firstElementChild.cloneNode(true)

      badge.querySelector("span").textContent = label
      badge.dataset.filterType = filter.type
      badge.dataset.filterValue = filter.value
      badge.setAttribute("aria-label", `Remove ${label} filter`)

      const item = document.createElement("li")
      item.className = "badge-aq-list-item"
      item.appendChild(badge)
      list.appendChild(item)
    })

    container.appendChild(list)
    this.updateFiltersMetaVisibility()
  }

  hasActiveFilters() {
    const search = this.searchFieldTarget?.value?.trim()
    const disciplines = this.filterFormTarget.querySelector("input[name='selected_disciplines']")?.value
    const purposes = this.filterFormTarget.querySelector("input[name='selected_purposes']")?.value
    const startDate = this.filterFormTarget.querySelector("input[name='start_date']")?.value?.trim()
    const endDate = this.filterFormTarget.querySelector("input[name='end_date']")?.value?.trim()

    return Boolean(
      search ||
      disciplines?.split(",").some(Boolean) ||
      purposes?.split(",").some(Boolean) ||
      startDate ||
      endDate
    )
  }

  updateFiltersMetaVisibility() {
    if (!this.hasClearFiltersButtonTarget) return

    this.clearFiltersButtonTarget.classList.toggle("hidden", !this.hasActiveFilters())
  }

  removeFilter(event) {
    event.preventDefault()
    const trigger = event.currentTarget.closest("[data-filter-type]")
    if (!trigger) return

    const type = trigger.dataset.filterType
    const value = trigger.dataset.filterValue
    const hiddenName = type === "discipline" ? "selected_disciplines" : "selected_purposes"
    const hiddenInput = this.filterFormTarget.querySelector(`input[name='${hiddenName}']`)
    const shell = hiddenInput?.closest("[data-controller~='railsui-dropdown-menu']")
    const row = shell?.querySelector(`[data-value="${value}"]`)
    const checkbox = row?.querySelector("input[type='checkbox']")

    if (checkbox) {
      checkbox.checked = false
      checkbox.dispatchEvent(new Event("change", { bubbles: true }))
      return
    }

    this.applyFilter()
  }

  toggleSelectAll(event) {
    if (event.target.checked) {
      this.selectAllShown()
    } else {
      this.deselectAllShown()
    }
  }

  selectAllShown() {
    if (!this.allAssessmentIds) return

    if (this.hasSelectedIdsFieldTarget) {
      this.selectedIds = new Set(this.allAssessmentIds)
      this.selectedIdsFieldTarget.value = this.allAssessmentIds.join(",")
    }

    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = true
    })

    this.updateViewResultsButtonState()
    this.updateSelectAllCheckboxState()
  }

  deselectAllShown() {
    if (!this.hasSelectedIdsFieldTarget) return

    this.selectedIds = new Set()
    this.selectedIdsFieldTarget.value = ""

    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = false
    })

    this.updateViewResultsButtonState()
    this.updateSelectAllCheckboxState()
  }

  updateSelectAllCheckboxState() {
    if (!this.hasSelectAllCheckboxTarget) return

    const assessmentIds = this.allAssessmentIds || []
    const allSelected = assessmentIds.length > 0 &&
      assessmentIds.every((id) => this.selectedIds.has(id))
    const someSelected = assessmentIds.some((id) => this.selectedIds.has(id))

    this.selectAllCheckboxTarget.checked = allSelected
    this.selectAllCheckboxTarget.indeterminate = !allSelected && someSelected
  }

  sort(event) {
    const trigger = event.currentTarget.closest("[data-sort-column]") || event.currentTarget
    const clickedColumn = trigger.dataset.sortColumn
    if (!clickedColumn) return

    const currentDirection = this.sortDirectionValue
    const currentColumn = this.sortColumnValue

    if (currentColumn === clickedColumn) {
      this.sortDirectionValue = currentDirection === "asc" ? "desc" : "asc"
    } else {
      this.sortDirectionValue = trigger.dataset.sortOrder || "asc"
    }

    this.sortColumnValue = clickedColumn
    this.syncSortAria(trigger)
    this.applyFilter()
  }

  sortKeydown(event) {
    const trigger = event.currentTarget.closest("[data-sort-column]") || event.currentTarget

    if (["Enter", " ", "Spacebar"].includes(event.key)) {
      event.preventDefault()
      this.sort(event)
      return
    }

    const sortButtons = Array.from(
      this.tableContentTarget.querySelectorAll("button.aqui-base-table__sort-button")
    )
    const currentIndex = sortButtons.indexOf(trigger)
    if (currentIndex < 0) return

    let nextIndex

    if (event.key === "ArrowRight") {
      nextIndex = (currentIndex + 1) % sortButtons.length
    } else if (event.key === "ArrowLeft") {
      nextIndex = (currentIndex - 1 + sortButtons.length) % sortButtons.length
    } else if (event.key === "Home") {
      nextIndex = 0
    } else if (event.key === "End") {
      nextIndex = sortButtons.length - 1
    } else {
      return
    }

    event.preventDefault()
    sortButtons[nextIndex].focus()
  }

  syncSortAria(trigger) {
    const headerCell = trigger.closest("th[aria-sort]")
    if (!headerCell) return

    const direction = this.sortDirectionValue === "asc" ? "ascending" : "descending"
    headerCell.setAttribute("aria-sort", direction)

    const columnLabel = trigger.dataset.columnLabel || headerCell.textContent.trim()
    trigger.setAttribute("aria-label", `Sort by ${columnLabel}`)

    this.announce(`Sorted by ${columnLabel}, ${direction}.`)
  }

  updateSelectedIds() {
    this.checkboxTargets.forEach((checkbox) => {
      const id = parseInt(checkbox.closest("tr").dataset.assessmentId, 10)
      if (checkbox.checked) {
        this.selectedIds.add(id)
      } else {
        this.selectedIds.delete(id)
      }
    })

    this.selectedIdsFieldTarget.value = [...this.selectedIds].join(",")
    this.updateViewResultsButtonState()
    this.updateSelectAllCheckboxState()
  }

  updateViewResultsButtonState() {
    if (!this.hasViewResultsButtonTarget) return

    const hasSelection = this.selectedIds.size > 0
    this.viewResultsButtonTarget.disabled = !hasSelection
    this.viewResultsButtonTarget.setAttribute("aria-disabled", String(!hasSelection))
  }

  redirectToSelectedResults(event) {
    if (event) event.preventDefault()

    const ids = this.selectedIdsFieldTarget.value.split(",").filter(Boolean)
    if (ids.length === 0) {
      this.announce("Please select at least one assessment.")
      return
    }

    this.navigateToReports(ids)
  }

  navigateToReports(ids) {
    const url = ids.length === 1
      ? `/assessment/reports/educator/cohort_progresses/${ids[0]}`
      : `/assessment/reports/educator/cohort_progresses?${ids.map((id) => `ids[]=${id}`).join("&")}`

    window.location.href = url
  }

  handleEscape(event) {
    if (event.key !== "Escape" || !this.isAssessmentModalOpen()) return

    event.preventDefault()
    this.close()
  }

  isAssessmentModalOpen() {
    const modalRoot = document.getElementById(this.modalIdValue)
    const container = modalRoot?.querySelector('[data-railsui-modal-target="container"]')
    return container?.getAttribute("aria-hidden") === "false"
  }

  announce(message) {
    if (!this.hasLiveRegionTarget || !message) return

    this.liveRegionTarget.textContent = ""
    requestAnimationFrame(() => {
      this.liveRegionTarget.textContent = message
    })
  }
}
